<script lang="ts">
  import type { DepartureGroup } from '../../types/DepartureGroup';
  import type { HafasDepartureRowJson } from '../../types/HafasDepartureRowJson';
  import { withUniqueKeys } from '../../lib/unique-keys';
  import DepartureRow from './departure-row.svelte';

  interface Properties {
    group: DepartureGroup;
  }

  const { group }: Properties = $props();

  /** Show at most five non-cancelled departures per direction. */
  const MAX_NON_CANCELLED = 5;
  /** Hard ceiling on rendered rows so a runaway feed (e.g. all cancelled) stays bounded. */
  const MAX_TOTAL_ROWS = 10;

  /** Walks up to `MAX_TOTAL_ROWS` from `bounded` and stops at (inclusive) the `MAX_NON_CANCELLED`th non-cancelled row. */
  function pickVisibleRows(rows: HafasDepartureRowJson[]): HafasDepartureRowJson[] {
    const bounded = rows.slice(0, MAX_TOTAL_ROWS);
    let running = 0;
    const cutoff = bounded.findIndex((entry) => {
      if (!entry.cancelled) running += 1;
      return running >= MAX_NON_CANCELLED;
    });
    return cutoff === -1 ? bounded : bounded.slice(0, cutoff + 1);
  }

  /** Trip identity — drops `index` and `effective` so a delay update doesn't remount the row and reset expand state. */
  function rowKey(entry: HafasDepartureRowJson): string {
    return `${entry.line}|${entry.headsign}|${entry.scheduled}`;
  }

  const ariaLabel = $derived(`Departures to ${group.label}`);
  const headerLabel = $derived(`→ ${group.label}`);
  const visibleRows = $derived(pickVisibleRows(group.rows));
  /** Rows keyed for `{#each}`: trip identity plus its occurrence in the group. */
  const keyedRows = $derived(withUniqueKeys(visibleRows, rowKey));
</script>

<!-- One destination bucket. Header names the shared next stop; rows below are
     capped at five non-cancelled (interspersed cancellations counted separately)
     with a total ceiling, sorted chronologically by effective time. -->
<section class="group" aria-label={ariaLabel}>
  <h3 class="group-label">{headerLabel}</h3>
  <ul class="rows" role="list">
    {#each keyedRows as entry, index (entry.key)}
      <li role="listitem">
        <DepartureRow row={entry.item} {index} />
      </li>
    {/each}
  </ul>
</section>

<style>
  .group {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: var(--space-sm) var(--space-md);
    margin-bottom: var(--space-sm);
  }

  .group-label {
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: var(--space-xs);
  }

  .rows {
    list-style: none;
    padding: 0;
    margin: 0;
  }
</style>
